import { useContext, useEffect, useState } from 'react'
import { Bank } from '~/entities'
import api from '../api'
import ErrorRow from '../components/ErrorRow'
import { jsonrpc } from '~/shared/api/jsonrpc'

const DepositForm = () => {
    const [amount, setAmount] = useState('')
    const [comment, setComment] = useState('')
    const [accept, setAccept] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState('')

    const onAmountChange = (e) => {
        setAmount(e.target.value)
        setError('')
    }

    const { reloadBalance } = useContext(Bank.Context)

    const deposit = async (e) => {
        e.preventDefault()
        setError(null)

        api.bank
            .depositBonus(amount, comment ?? null)
            .then(() => {
                reloadBalance()
                setAmount('')
                setComment('')
                setAccept(false)
                setSuccess(true)
                setTimeout(() => setSuccess(false), 2000)
            })
            .catch((data) => setError(`Error: ${data.error ?? 'Error'}`))
    }

    return (
        <div className='flex flex-col gap-2 w-max max-w-full mx-auto border border-white border-opacity-20 rounded p-4'>
            <form onSubmit={deposit} className='flex flex-col flex-wrap items-center gap-4 '>
                <header className='text-sm opacity-50'>
                    Deposit <span className='font-bold'>USDC</span>
                </header>
                <input
                    value={amount}
                    onChange={onAmountChange}
                    type='number'
                    min={0}
                    step={0.1}
                    placeholder='amount'
                    className='input-team-tools w-full'
                />
                <textarea
                    placeholder='comment'
                    onChange={(e) => setComment(e.target.value)}
                    maxLength={255}
                    className='input-team-tools w-full text-sm'
                />
                <label className='flex flex-row items-center gap-2 cursor-pointer pt-2'>
                    <input type='checkbox' checked={accept} onChange={(e) => setAccept(e.target.checked)} />
                    <span className='opacity-75 text-sm'>I understand that this action will change BR1 balance</span>
                </label>
                {success ? (
                    <div className=' text-green-600'>Deposited</div>
                ) : (
                    <button
                        disabled={!amount || !accept}
                        className='hover:text-[#C91FFF] disabled:cursor-not-allowed disabled:hover:text-inherit disabled:opacity-20 font-bold'
                    >
                        Deposit
                    </button>
                )}
            </form>

            <ErrorRow message={error} />
        </div>
    )
}

type DepositSummaryRow = { kind: 'house' | 'player'; symbol: string; count: number; amount: number }

type DepositScope = 'house' | 'player'

const SCOPES: Array<[DepositScope, string]> = [
    ['house', 'House top-ups onto br1'],
    ['player', 'Player deposits'],
]

/** Counts and totals per currency, never added across currencies.
 *
 *  The list used to head itself "Total: N", where N counted player deposits,
 *  SOL deposits and the house's own top-ups together (audit BANK-07). */
const DepositSummary = ({ rows, scope }: { rows: DepositSummaryRow[]; scope: DepositScope }) => {
    const mine = rows.filter((r) => r.kind === scope)
    if (mine.length === 0) return <div className='text-white/20 text-xs text-center'>none</div>
    return (
        <div className='flex flex-row flex-wrap gap-4 justify-center text-xs'>
            {mine.map((r) => (
                <div key={r.symbol} className='flex flex-row gap-x-2'>
                    <span className='text-white/40'>{r.symbol}:</span>
                    <span className='font-bold'>{Number(r.amount).toLocaleString()}</span>
                    <span className='text-white/40'>in {r.count.toLocaleString()} rows</span>
                </div>
            ))}
        </div>
    )
}

const initialList = {
    data: [],
    offset: undefined,
    count: undefined,
    has_more: undefined,
    max_id: undefined,
}

// br1 is user -1. Player deposits are every other account.
const scopeFilter = (s: DepositScope) => (s === 'house' ? { user: '-1' } : { user_id__gt: 0 })

const TransactionsList = () => {
    const [scope, setScope] = useState<DepositScope>('house')
    const [summary, setSummary] = useState<DepositSummaryRow[]>([])
    const [transactions, setTransactions] = useState(initialList)
    const [loading, setLoading] = useState(false)

    const load = async (from = transactions, s = scope) => {
        setLoading(true)
        jsonrpc('bank.transactions', {
            offset: from.offset,
            // The anchor from the first page, so rows written while the list
            // is open cannot shift later pages (audit BANK-36).
            max_id: from.max_id ?? null,
            filters: { operation__in: [1], ...scopeFilter(s) },
        })
            .then((response) => {
                setTransactions({ ...response.result, data: [...from.data, ...response.result.data] })
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        jsonrpc('bank.deposit_summary', {}).then((response) => setSummary(response?.result ?? []))
    }, [])

    useEffect(() => {
        setTransactions(initialList)
        load(initialList, scope)
    }, [scope])

    return (
        <div className='flex flex-col gap-y-4 w-full'>
            <nav className='flex flex-row gap-4 justify-center text-sm'>
                {SCOPES.map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setScope(key)}
                        className={scope === key ? 'border-b-2 font-bold' : 'text-white/50 hover:text-violet-500'}
                    >
                        {label}
                    </button>
                ))}
            </nav>
            <DepositSummary rows={summary} scope={scope} />
            <header className='flex flex-row gap-x-2 items-center justify-center text-xs'>
                <span className='text-white/20'>rows:</span>
                {transactions.count}
            </header>
            {transactions.data.map((t, i) => (
                <div key={t.id} className='flex flex-col w-full text-xs'>
                    <span className='text-white/20'>#{i + 1}</span>
                    <div className='flex flex-col gap-y-2 border rounded border-white/20 p-2 font-bold'>
                        <div>
                            <span className='text-white/50 font-normal'>id:</span> {t.id}
                        </div>
                        <div>
                            <span className='text-white/50 font-normal'>account:</span> {t.username}
                        </div>
                        <div>
                            <span className='text-white/50 font-normal'>operation:</span>{' '}
                            <span className='text-green-500'>Deposit</span>
                        </div>
                        <div>
                            {/* The row's own currency. A SOL deposit's amount is
                                SOL, and it used to read "USDC: 0.5" (audit BANK-10). */}
                            <span className='text-white/50 font-normal'>{t.currency_symbol ?? 'amount'}:</span>{' '}
                            {t.amount}
                        </div>
                        <div>
                            <span className='text-white/50 font-normal'>created by:</span> {t.initiator}
                        </div>
                        <div>
                            <span className='text-white/50 font-normal'>created at:</span>{' '}
                            {new Date(t.created_at).toLocaleString()}
                        </div>
                        <div>
                            <span className='text-white/50 font-normal'>comment:</span> {t.comment}
                        </div>
                    </div>
                </div>
            ))}
            <footer className='flex flex-row justify-center'>
                {transactions.has_more && (
                    <button disabled={loading} onClick={() => load()}>
                        LOAD PREVIOUS
                    </button>
                )}
            </footer>
        </div>
    )
}

export const BankDeposit = () => {
    return (
        <div className='flex flex-col items-center gap-8 w-full'>
            <DepositForm />
            <TransactionsList />
        </div>
    )
}
